import { z } from "zod";
import {
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { InvalidOptionsError } from "@utils/InvalidOptionsError";
import type { UseInfiniteQueryOptionsFromConfig } from "@utils/queryConfigs";
import { RouterOutputs, trpc, type ReactQueryOptions } from "@utils/trpc";
import {
  artistProfileSchema,
  artistSchema,
  type Artist,
  type ArtistProfile,
} from "@libs/wire";
import { useData } from "../../runtime/provider";
import type { DataSession } from "../../data/core/session";
import { infiniteDataSchema } from "@utils/durableCache/schemas";
import { cachedArtists } from "@sqlite/schema";
import type { InfiniteData } from "@tanstack/react-query";
import { waitForAuthReady } from "@utils/auth/waitForAuthReady";

type QueryOptions = Omit<
  ReactQueryOptions["catalog"]["artists"]["get"],
  "suspense"
>;

/**
 * Bidirectional drift check vs the wire shape — see `useSongs.ts` for the
 * rationale.
 */
type _ArtistRouterShape = RouterOutputs["catalog"]["artists"]["get"][number];
type _ArtistWireSchemaSubset = Artist extends _ArtistRouterShape ? true : never;
type _ArtistWireSchemaSuperset = _ArtistRouterShape extends Artist
  ? true
  : never;
const _artistWireSchemaDriftCheck: [
  _ArtistWireSchemaSubset,
  _ArtistWireSchemaSuperset,
] = [true, true];
void _artistWireSchemaDriftCheck;

export const artistsQuery = (data: DataSession, ids?: string[]) => {
  const artistIds = ids ?? [];
  return data.cache.durableEntityQueryOptions<Artist>({
    queryKey: getQueryKey(trpc.catalog.artists.get, ids),
    ids: artistIds,
    entityTable: cachedArtists,
    idColumn: cachedArtists.artistId,
    dataColumn: cachedArtists.data,
    fetch: async (fetchIds) => data.utils.catalog.artists.get.fetch(fetchIds),
    schema: artistSchema,
    getId: (a) => a.id,
    label: "useArtists",
  });
};

/**
 * Compile-time drift check for `catalog.artists.getProfileById` —
 * `artistProfileSchema` and the router output type must be mutually
 * assignable. See useLibrary/queryFunctions.ts for rationale.
 */
type ArtistProfileRouterOutput =
  RouterOutputs["catalog"]["artists"]["getProfileById"];
type _ArtistProfileWireSchemaSubset =
  z.infer<typeof artistProfileSchema> extends ArtistProfileRouterOutput
    ? true
    : never;
type _ArtistProfileWireSchemaSuperset =
  ArtistProfileRouterOutput extends z.infer<typeof artistProfileSchema>
    ? true
    : never;
const _artistProfileWireSchemaDriftCheck: [
  _ArtistProfileWireSchemaSubset,
  _ArtistProfileWireSchemaSuperset,
] = [true, true];
void _artistProfileWireSchemaDriftCheck;

export const artistProfileQueryKey = (artistId?: string) =>
  getQueryKey(trpc.catalog.artists.getProfileById, artistId);

export type ArtistProfileQueryData = ArtistProfileRouterOutput;

export const artistProfileQuery = (data: DataSession, artistId: string) =>
  data.cache.durableQueryOptions({
    queryKey: artistProfileQueryKey(artistId),
    queryFn: async (): Promise<ArtistProfile> => {
      if (!artistId) {
        throw new InvalidOptionsError("artistProfileQuery");
      }
      // Public but viewer-sensitive: wait until a restoring Firebase session
      // can attach its token instead of silently caching an anonymous profile.
      await waitForAuthReady(data.auth);
      return data.api.catalog.artists.getProfileById.query(artistId);
    },
    schema: artistProfileSchema,
  });

export const useArtists = (ids?: string[], options?: QueryOptions) => {
  const data = useData();
  const query = useQuery({
    ...artistsQuery(data, ids),
    ...options,
    enabled: options?.enabled !== undefined ? options?.enabled : true,
  });

  return { query };
};

export const useArtistProfile = (
  id?: string,
  options?: Omit<
    ReactQueryOptions["catalog"]["artists"]["getProfileById"],
    "suspense"
  >,
) => {
  const data = useData();
  const query = useQuery({
    ...artistProfileQuery(data, id || ""),
    ...options,
    enabled: options?.enabled !== undefined ? options?.enabled : true,
  });

  return { query };
};

export const useArtistProfileSuspense = (
  id: string,
  options?: Omit<
    ReactQueryOptions["catalog"]["artists"]["getProfileById"] | null,
    "suspense"
  >,
) => {
  const data = useData();
  const config = artistProfileQuery(data, id);
  const suspenseQuery = useSuspenseQuery({
    queryKey: config.queryKey,
    queryFn: async () => {
      if (!id) return null;
      return config.queryFn();
    },
    initialData: config.initialData,
    initialDataUpdatedAt: config.initialDataUpdatedAt,
    networkMode: config.networkMode,
    ...options,
  });

  return { query: suspenseQuery };
};

type ArtistsListPaginatedInput = {
  searchTerm?: string;
  limit?: number;
};

type ArtistsListPaginatedPage =
  RouterOutputs["catalog"]["artists"]["listPaginated"];

const artistsListPaginatedPageSchema = z.object({
  data: z.array(artistSchema),
  nextCursor: z.number().optional(),
});

// Drift check: the page wire schema must match the router output.
type _ArtistsListPaginatedSubset =
  z.infer<
    typeof artistsListPaginatedPageSchema
  > extends ArtistsListPaginatedPage
    ? true
    : never;
type _ArtistsListPaginatedSuperset =
  ArtistsListPaginatedPage extends z.infer<
    typeof artistsListPaginatedPageSchema
  >
    ? true
    : never;
const _artistsListPaginatedDrift: [
  _ArtistsListPaginatedSubset,
  _ArtistsListPaginatedSuperset,
] = [true, true];
void _artistsListPaginatedDrift;

const artistsListPaginatedInfiniteSchema = infiniteDataSchema(
  artistsListPaginatedPageSchema,
  z.number(),
);

type ArtistsListPaginatedQueryOptions = Omit<
  UseInfiniteQueryOptionsFromConfig<
    ReturnType<typeof artistsListPaginatedQuery>
  >,
  "initialPageParam" | "getNextPageParam"
>;

export const artistsListPaginatedQuery = (
  data: DataSession,
  input?: ArtistsListPaginatedInput,
) => ({
  queryKey: getQueryKey(trpc.catalog.artists.listPaginated, input),
  queryFn: ({ pageParam }: { pageParam?: number }) => {
    return data.utils.catalog.artists.listPaginated.fetch({
      ...input,
      cursor: pageParam ?? 0,
    });
  },
  getNextPageParam: (
    lastPage: RouterOutputs["catalog"]["artists"]["listPaginated"],
  ) => {
    return lastPage.nextCursor ?? undefined;
  },
  initialPageParam: 0,
});

export const useArtistsListPaginated = (
  input?: ArtistsListPaginatedInput,
  options?: ArtistsListPaginatedQueryOptions,
) => {
  const data = useData();
  const enabled = options?.enabled !== undefined ? options.enabled : true;
  const baseConfig = artistsListPaginatedQuery(data, input);

  // Cast through `any` to reconcile the typed `pageParam: number` queryFn
  // with react-query's `pageParam: unknown` constraint when meta +
  // initialData (from durableInfiniteOptions) are present. Same pattern
  // as useArtistStoreAccesses — see notes there.
  const queryOptions = {
    ...baseConfig,
    ...data.cache.durableInfiniteOptions<ArtistsListPaginatedPage>(
      baseConfig.queryKey,
      artistsListPaginatedInfiniteSchema as unknown as z.ZodType<
        InfiniteData<ArtistsListPaginatedPage>
      >,
      {
        decompose: (data: InfiniteData<ArtistsListPaginatedPage>) =>
          data.pages.flatMap((p) => p.data),
        decomposeTable: cachedArtists,
        decomposeIdField: "id",
        decomposeSchema: artistSchema,
      },
    ),
    ...options,
    enabled,
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const query = useInfiniteQuery(queryOptions as any) as ReturnType<
    typeof useInfiniteQuery<
      ArtistsListPaginatedPage,
      Error,
      InfiniteData<ArtistsListPaginatedPage>
    >
  >;

  return { query };
};
